import React, { useState } from 'react';
import { Dumbbell, Activity, Trash2 } from 'lucide-react';

const CalendarView = ({ sessions, exercises, onSelectDate, selectedDate, onDeleteSession }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  
  const getSessionsForDate = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return sessions.filter(s => s.date === dateStr);
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 shadow-2xl">
      <div className="flex justify-between items-center mb-6">
        <button onClick={prevMonth} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all">
          ←
        </button>
        <h2 className="text-2xl font-bold">{monthName}</h2>
        <button onClick={nextMonth} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all">
          →
        </button>
      </div>

      <div className="grid grid-cols-7 gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center font-semibold text-slate-400 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: startingDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}
        
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const daySessions = getSessionsForDate(day);
          const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isSelected = selectedDate === dateStr;
          
          return (
            <div
              key={day}
              onClick={() => onSelectDate(isSelected ? null : dateStr)}
              className={`aspect-square p-2 rounded-lg border-2 cursor-pointer transition-all ${
                isSelected 
                  ? 'border-blue-400 bg-blue-900/50 shadow-lg shadow-blue-500/50' 
                  : daySessions.length > 0 
                    ? 'border-green-500 bg-green-900/30 hover:bg-green-900/50' 
                    : 'border-slate-600 bg-slate-700/30 hover:bg-slate-700/50'
              }`}
            >
              <div className="font-semibold mb-1">{day}</div>
              {daySessions.length > 0 && (
                <div className="space-y-1">
                  {daySessions.map(session => {
                    const exercise = exercises.find(e => e.id === session.exerciseId);
                    return (
                      <div key={session.id} className="text-xs flex items-center gap-1">
                        {exercise?.type === 'strength' ? (
                          <Dumbbell className="w-3 h-3" />
                        ) : (
                          <Activity className="w-3 h-3" />
                        )}
                        <span className="truncate">{exercise?.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedDate && (
        <div className="mt-6 bg-slate-900/50 rounded-lg p-6">
          <h3 className="text-xl font-bold mb-4">Sessions on {selectedDate}</h3>
          <div className="space-y-4">
            {sessions
              .filter(s => s.date === selectedDate)
              .map(session => {
                const exercise = exercises.find(e => e.id === session.exerciseId);
                return (
                  <div key={session.id} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        {exercise?.type === 'strength' ? (
                          <Dumbbell className="w-5 h-5 text-blue-400" />
                        ) : (
                          <Activity className="w-5 h-5 text-green-400" />
                        )}
                        <h4 className="text-lg font-semibold">{exercise?.name}</h4>
                      </div>
                      <button
                        onClick={() => onDeleteSession(session.id)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    
                    {session.warmup && (
                      <div className="mb-3 text-sm">
                        <span className="text-slate-400">Warmup:</span> {session.warmup}
                      </div>
                    )}

                    {exercise?.type === 'strength' && session.sets && (
                      <div className="space-y-2">
                        {session.sets.map((set, idx) => (
                          <div key={idx} className="flex gap-4 text-sm bg-slate-900/50 p-2 rounded">
                            <span className="text-slate-400">Set {idx + 1}:</span>
                            <span>{set.weight} kg × {set.reps} reps</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {exercise?.avgSpeedMetric && session.avgSpeed && (
                      <div className="text-sm">
                        <span className="text-slate-400">Average Speed:</span> {session.avgSpeed} km/h
                      </div>
                    )}

                    {session.duration && (
                      <div className="text-sm mt-2">
                        <span className="text-slate-400">Duration:</span> {session.duration} min
                      </div>
                    )}

                    {session.distance && (
                      <div className="text-sm mt-2">
                        <span className="text-slate-400">Distance:</span> {session.distance} km
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
